import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  Table,
  Checkbox,
  Select,
  InputNumber,
  Upload,
  Button,
  DatePicker,
  Input,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../core/data/redux/store";
import {
  createTdsDeclaration,
  fetchTdsDeclarations,
  updateTdsDeclaration,
  deleteTdsDeclaration,
  fetchTdsDeclarationsByEmployeeId, // ⬅ add delete for diffing
} from "../../../../core/data/redux/payroll/TDS/tdsDelcaration/tdsDeclarationAction";
import { getEmployeeByUserId } from "../../../../core/data/redux/employee/employeeAction";
import { fetchFinancialYearsByCompanyId } from "../../../../core/data/redux/financialYear/financialYearActions";

type Seniority = "<60" | ">=60";

type SectionRow = {
  key: string;
  SectionCode: string;
  Label: string;
  SubType?: string;
  usesSeniority?: boolean;
  seniority?: Seniority;
  selected: boolean;
  proofSubmitted: boolean;
  maxAmount?: number | null;
  claimedAmount?: number;
  file?: File | null;
  variableNote?: string;

  existingFileName?: string | null;
  existingFileUrl?: string | null;
  // Existing declaration id if present (to know create vs update vs delete)
  DeclarationID?: number | null;
};

const SECTIONS_OLD_2024_25: SectionRow[] = [
  {
    key: "80C",
    SectionCode: "80C",
    Label: "Section 80C (Investments)",
    SubType: "N/A",
    selected: false,
    proofSubmitted: false,
    maxAmount: 150000,
    claimedAmount: 0,
    file: null,
  },
  {
    key: "80D_self",
    SectionCode: "80D",
    Label: "Section 80D – Self/Family (Health Insurance)",
    SubType: "Self/Family",
    usesSeniority: true,
    seniority: "<60",
    selected: false,
    proofSubmitted: false,
    maxAmount: 25000,
    claimedAmount: 0,
    file: null,
  },
  {
    key: "80D_parents",
    SectionCode: "80D",
    Label: "Section 80D – Parents (Health Insurance)",
    SubType: "Parents",
    usesSeniority: true,
    seniority: "<60",
    selected: false,
    proofSubmitted: false,
    maxAmount: 25000,
    claimedAmount: 0,
    file: null,
  },
  {
    key: "80TTA",
    SectionCode: "80TTA",
    Label: "Section 80TTA (Savings Interest – Non-Seniors)",
    SubType: "N/A",
    selected: false,
    proofSubmitted: false,
    maxAmount: 10000,
    claimedAmount: 0,
    file: null,
  },
  {
    key: "24B",
    SectionCode: "24B",
    Label: "Section 24(b) (Home Loan Interest – Self-occupied)",
    SubType: "N/A",
    selected: false,
    proofSubmitted: false,
    maxAmount: 200000,
    claimedAmount: 0,
    file: null,
  },
  {
    key: "Monthly House Rent",
    SectionCode: "MHR",
    Label: "Monthly House Rent",
    SubType: "N/A",
    selected: false,
    proofSubmitted: false,
    maxAmount: null,
    variableNote: "Monthly Actual Rent",
    claimedAmount: 0,
    file: null,
  },
  {
    key: "HRA",
    SectionCode: "HRA",
    Label: "HRA (House Rent Allowance) – Exemption",
    SubType: "N/A",
    selected: false,
    proofSubmitted: false,
    maxAmount: null,
    variableNote: "Variable (depends on salary, rent, city & actuals)",
    claimedAmount: 0,
    file: null,
  },
  {
    key: "LTA",
    SectionCode: "LTA",
    Label: "LTA (Leave Travel Allowance) – Exemption",
    SubType: "N/A",
    selected: false,
    proofSubmitted: false,
    maxAmount: null,
    variableNote: "Variable (as per policy & proofs)",
    claimedAmount: 0,
    file: null,
  },
  {
    key: "80G",
    SectionCode: "80G",
    Label: "Section 80G (Donations)",
    SubType: "N/A",
    selected: false,
    proofSubmitted: false,
    maxAmount: null,
    variableNote:
      "Variable (50%/100% With/Without Limit based on institution)",
    claimedAmount: 0,
    file: null,
  },
];

const getRowKey = (sec: string, sub?: string) =>
  sec === "80D"
    ? `${sec}_${sub === "Parents" ? "parents" : "self"}`
    : sec;

const getDynamicMax = (row: SectionRow): number | null => {
  if (row.SectionCode === "80D" && row.usesSeniority) {
    return row.seniority === ">=60" ? 50000 : 25000;
  }
  return typeof row.maxAmount === "number" ? row.maxAmount : null;
};

const DeclarationFormModal: React.FC<any> = ({
  modalType, // "create" | "edit"
  isModalVisible,
  handleCancel,
  selectedRowData, // in EDIT we’ll receive { EmployeeID, FinancialYearID } from the table
  setTableKey,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((s: any) => s.auth);
  const { activeCompany } = useSelector((s: any) => s.companyDetails);
  const { financialYearsByCompany } = useSelector((s: any) => s.financialYears);
  const { tdsDeclaration } = useSelector((s: any) => s.TDS || {});
  const employeeDeclarations: any[] =
    tdsDeclaration?.employeeDeclarations || [];

  const companyId = activeCompany?.company_detail_id;
  const { employeesbyUser } = useSelector((s: any) => s.employee);
  const employeeId = employeesbyUser?.employeeId;

  // Header-level
  const [financialYearId, setFinancialYearId] = useState<number | null>(null);
  const [declarationDate, setDeclarationDate] = useState<dayjs.Dayjs | null>(
    dayjs()
  );
  const [description, setDescription] = useState<string>("");

  const [rows, setRows] = useState<SectionRow[]>(
    () => SECTIONS_OLD_2024_25.map((r) => ({ ...r }))
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user?.UserId) {
      dispatch(getEmployeeByUserId({ userId: user?.UserId }));
    }
  }, [dispatch, user?.UserId]);

  useEffect(() => {
    if (companyId) {
      dispatch(fetchFinancialYearsByCompanyId(companyId));
    }
  }, [dispatch, companyId]);

  // Helper: all existing lines for current employee + FY
  const existingForFY = useMemo(() => {
    if (!employeeId || !financialYearId) return [];
    return (employeeDeclarations || []).filter(
      (d) =>
        String(d.EmployeeID) === String(employeeId) &&
        String(d.FinancialYearID) === String(financialYearId)
    );
  }, [employeeDeclarations, employeeId, financialYearId]);

  // When opening in EDIT, preselect FY & hydrate all sections from existing lines
  useEffect(() => {
    if (modalType === "edit" && selectedRowData) {
      const fy = Number(selectedRowData.FinancialYearID);
      setFinancialYearId(isNaN(fy) ? null : fy);
      setDeclarationDate(
        selectedRowData.DeclarationDate
          ? dayjs(selectedRowData.DeclarationDate)
          : dayjs()
      );
      setDescription(selectedRowData.Description || "");
    } else if (modalType === "create") {
      setFinancialYearId(null);
      setDeclarationDate(dayjs());
      setDescription("");
      setRows(SECTIONS_OLD_2024_25.map((r) => ({ ...r })));
      setErrors({});
    }
  }, [modalType, selectedRowData]);

  // Hydrate rows from existing lines (runs when FY ready or dataset updates)
  useEffect(() => {
    if (!financialYearId) return;

    const next = SECTIONS_OLD_2024_25.map((template) => {
      const match = existingForFY.find((d) => {
        if (template.SectionCode !== d.SectionCode) return false;
        // map SubType
        const sub = d.SubType || "N/A";
        return (template.SubType || "N/A") === sub;
      });

      let row: SectionRow = { ...template, DeclarationID: null };

      if (match) {
        // selected + prefilled
        row.selected = true;
        row.proofSubmitted = (match.ProofStatus || "").toLowerCase() === "submitted";
        row.claimedAmount = Number(match.DeclaredAmount) || 0;
        row.DeclarationID = Number(match.DeclarationID) || null;

        row.existingFileName = match.FileName || null;
        row.existingFileUrl = match.FilePath || null;

        // Seniority max recalculation for 80D (don’t try to infer seniority from data)
        if (row.usesSeniority) {
          row.maxAmount = getDynamicMax(row);
          if (
            typeof row.maxAmount === "number" &&
            (row.claimedAmount ?? 0) > row.maxAmount
          ) {
            row.claimedAmount = row.maxAmount;
          }
        }
      }
      return row;
    });

    setRows(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialYearId, existingForFY.length]);

  const validate = () => {
    const newErr: Record<string, string> = {};
    if (!financialYearId)
      newErr["FinancialYearID"] = "Financial Year is required.";
    if (!declarationDate)
      newErr["DeclarationDate"] = "Declaration Date is required.";

    const selected = rows.filter((r) => r.selected);
    if (selected.length === 0) newErr["Rows"] = "Select at least one section.";

    selected.forEach((r) => {
      const max = getDynamicMax(r);
      const claimed = r.claimedAmount ?? 0;
      if (claimed <= 0) {
        newErr[`claimed_${r.key}`] = "Claimed amount must be greater than 0.";
      }
      if (typeof max === "number" && claimed > max) {
        newErr[`claimed_${r.key}`] = `Cannot exceed max of ₹${max.toLocaleString(
          "en-IN"
        )}.`;
      }
    });

    setErrors(newErr);
    return Object.keys(newErr).length === 0;
  };

  const setRow = (key: string, patch: Partial<SectionRow>) => {
    setRows((prev) => {
      const copy = [...prev];
      const idx = copy.findIndex((r) => r.key === key);
      if (idx >= 0) {
        const merged = { ...copy[idx], ...patch };
        if (merged.usesSeniority) {
          merged.maxAmount = getDynamicMax(merged);
          if (
            typeof merged.maxAmount === "number" &&
            (merged.claimedAmount ?? 0) > merged.maxAmount
          ) {
            merged.claimedAmount = merged.maxAmount;
          }
        }
        copy[idx] = merged;
      }
      return copy;
    });
  };

  const columns: ColumnsType<SectionRow> = [
    {
      title: "",
      dataIndex: "selected",
      width: 48,
      render: (_val, record) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) => setRow(record.key, { selected: e.target.checked })}
        />
      ),
    },
    {
      title: "Section / Type",
      dataIndex: "Label",
      render: (_val, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontWeight: 600 }}>{record.Label}</div>
          <div style={{ fontSize: 12, color: "#666" }}>
            Code: <b>{record.SectionCode}</b>
            {record.SubType && record.SubType !== "N/A"
              ? <>{` • ${record.SubType}`}</>
              : null}
          </div>
          {record.usesSeniority && (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 12, color: "#666" }}>Seniority:</span>
              <Select<Seniority>
                size="small"
                value={record.seniority || "<60"}
                style={{ width: 120 }}
                onChange={(val) => setRow(record.key, { seniority: val })}
                options={[
                  { value: "<60", label: "< 60 years" },
                  { value: ">=60", label: "≥ 60 years" },
                ]}
                disabled={!record.selected}
              />
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Proof submitted",
      dataIndex: "proofSubmitted",
      width: 150,
      align: "center",
      render: (_val, record) => (
        <Checkbox
          checked={record.proofSubmitted}
          onChange={(e) => setRow(record.key, { proofSubmitted: e.target.checked })}
          disabled={!record.selected}
        />
      ),
    },
    {
      title: "Max amount",
      dataIndex: "maxAmount",
      width: 160,
      render: (_val, record) => {
        const max = getDynamicMax(record);
        return (
          <div>
            {typeof max === "number" ? (
              <>₹{max.toLocaleString("en-IN")}</>
            ) : (
              <span style={{ color: "#8c8c8c" }}>
                {record.variableNote || "—"}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Claimed amount",
      dataIndex: "claimedAmount",
      width: 200,
      render: (_val, record) => {
        const max = getDynamicMax(record);
        return (
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            value={record.claimedAmount}
            onChange={(val) =>
              setRow(record.key, { claimedAmount: Number(val || 0) })
            }
            max={typeof max === "number" ? max : undefined}
            disabled={!record.selected}
          />
        );
      },
    },
    {
      title: "Upload document",
      dataIndex: "file",
      width: 300,
      render: (_val, record) => {
        const disabled = !record.selected;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {/* Existing file (from server) */}

            {/* Uploader */}
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Upload
                beforeUpload={() => false}
                maxCount={1}
                onChange={({ fileList }) => {
                  const file = fileList[0]?.originFileObj as File | null;
                  setRow(record.key, { file });
                }}
                disabled={disabled}
                showUploadList={false}
              >
                <Button
                  icon={<UploadOutlined />}
                  disabled={disabled}
                  className={record.file || record.existingFileName ? "mt-4" : ""}
                >
                  {record.file ? "Replace file" : (record.existingFileName ? "Replace file" : "Select file")}
                </Button>
              </Upload>

              {/* Now this does NOT open the picker */}
              {record.file &&
                <Button
                  size="small"
                  type="link"
                  onClick={() => setRow(record.key, { file: null })}
                  style={{ paddingLeft: 8 }}
                  className="mt-4"
                >
                  Remove
                </Button>
              }
            </div>

            {record.existingFileName && record.existingFileUrl && !record.file && (
              <div style={{ fontSize: 12 }}>
                <span style={{ color: "#666" }}>Existing: </span>
                <a href={record.existingFileUrl} target="_blank" rel="noreferrer">
                  {record.existingFileName}
                </a>
              </div>
            )}

            {/* If user has picked a new file in this session, show that name */}
            {record.file && (
              <div style={{ fontSize: 12 }}>
                <span style={{ color: "#666" }}>Selected: </span>
                <span>{record.file.name}</span>

              </div>
            )}
          </div>
        );
      },
    }

  ];

  const handleSubmit = async () => {
    if (!validate()) return;

    // Prevent duplicate "create" if FY already has lines
    if (
      modalType === "create" &&
      existingForFY.length > 0
    ) {
      message.error(
        "A declaration already exists for this Financial Year. Please edit the existing one."
      );
      return;
    }

    const basePayload = {
      FinancialYearID: financialYearId,
      DeclarationDate: declarationDate
        ? dayjs(declarationDate).format("YYYY-MM-DD")
        : null,
      CreatedBy: user?.UserId,
      ModifiedBy: user?.UserId,
      EmployeeID: employeeId,
      Description: description || "",
    };

    const selectedRows = rows.filter(
      (r) => r.selected && (r.claimedAmount ?? 0) > 0
    );

    // Build maps for diff (existing vs UI)
    const existingMap = new Map<
      string,
      { id: number; SectionCode: string; SubType: string; DeclaredAmount: number; ProofStatus: string }
    >();

    existingForFY.forEach((d) => {
      const key = getRowKey(d.SectionCode, d.SubType || "N/A");
      existingMap.set(key, {
        id: Number(d.DeclarationID),
        SectionCode: d.SectionCode,
        SubType: d.SubType || "N/A",
        DeclaredAmount: Number(d.DeclaredAmount) || 0,
        ProofStatus: (d.ProofStatus || "").toLowerCase(),
      });
    });

    const uiMap = new Map<
      string,
      { SectionCode: string; SubType: string; claimed: number; proof: boolean; file?: File | null }
    >();
    selectedRows.forEach((r) => {
      uiMap.set(r.key, {
        SectionCode: r.SectionCode,
        SubType: r.SubType || "N/A",
        claimed: Number(r.claimedAmount || 0),
        proof: !!r.proofSubmitted,
        file: r.file || null,
      });
    });

    const toCreate: any[] = [];
    const toUpdate: any[] = [];
    const toDelete: number[] = [];

    // Decide creates/updates
    uiMap.forEach((val, key) => {
      const exist = existingMap.get(key);
      const payloadLine = {
        ...basePayload,
        SectionCode: val.SectionCode,
        SubType: val.SubType,
        DeclaredAmount: val.claimed,
        ProofSubmittedAmount: val.proof ? val.claimed : 0,
        VerifiedAmount: 0,
        ProofStatus: val.proof ? "submitted" : "not_submitted",
        VerificationStatus: "pending",
        FromMonth: null,
        ToMonth: null,
        DocumentRef: "",
        file: val.file || null,
      };

      if (!exist) {
        toCreate.push(payloadLine);
      } else {
        const changed =
          exist.DeclaredAmount !== val.claimed ||
          exist.ProofStatus !== (val.proof ? "submitted" : "not_submitted");
        if (changed || val.file) {
          toUpdate.push({ id: exist.id, ...payloadLine });
        }
        existingMap.delete(key);
      }
    });

    existingMap.forEach((v) => {
      toDelete.push(v.id);
    });

    let failed = false;

    for (const c of toCreate) {
      console.log({ c })
      const res = await dispatch(createTdsDeclaration(c));
      if (res.meta.requestStatus !== "fulfilled") failed = true;
    }
    for (const u of toUpdate) {
      console.log({ u })
      const res = await dispatch(updateTdsDeclaration(u));
      if (res.meta.requestStatus !== "fulfilled") failed = true;
    }
    for (const delId of toDelete) {
      console.log({ delId })
      const res = await dispatch(deleteTdsDeclaration(delId));
      if (res.meta.requestStatus !== "fulfilled") failed = true;
    }
    if (employeeId) {
      dispatch(fetchTdsDeclarationsByEmployeeId(employeeId));
    }

    if (!failed) {
      message.success(
        modalType === "edit"
          ? "Declaration updated successfully"
          : "Declarations created successfully"
      );
      await dispatch(fetchTdsDeclarations());
      setTableKey?.((k: any) => k + 1);
      handleCancel();
    } else {
      message.warning("Some operations failed. Please review and retry.");
    }
  };

  return (
    <Drawer
      title={
        modalType === "edit"
          ? "Edit Declarations (Old Regime 2024–25)"
          : "Add Declarations (Old Regime 2024–25)"
      }
      style={{ marginTop: 50, paddingBottom: 50 }}
      placement="right"
      width={900}
      onClose={handleCancel}
      open={isModalVisible}
      destroyOnClose
    >
      <div className="row mb-3">
        <div className="col-md-4">
          <label className="form-label">
            Financial Year <span className="text-danger">*</span>
          </label>
          <Select
            value={financialYearId}
            onChange={(val) => setFinancialYearId(val)}
            className="w-100"
            placeholder="Select financial year"
            allowClear
            options={(financialYearsByCompany || []).map((fy: any) => ({
              value: fy.id,
              label: fy.name,
            }))}
          />
          {errors.FinancialYearID && (
            <div className="text-danger">{errors.FinancialYearID}</div>
          )}
        </div>

        <div className="col-md-4">
          <label className="form-label">
            Declaration Date <span className="text-danger">*</span>
          </label>
          <DatePicker
            value={declarationDate}
            onChange={(date) => setDeclarationDate(date)}
            className="w-100"
          />
          {errors.DeclarationDate && (
            <div className="text-danger">{errors.DeclarationDate}</div>
          )}
        </div>
      </div>

      {errors.Rows && <div className="text-danger mb-2">{errors.Rows}</div>}

      <Table<SectionRow>
        rowKey="key"
        columns={columns}
        dataSource={rows}
        pagination={false}
        size="middle"
      />

      {/* Inline row errors */}
      <div className="mt-2">
        {rows.map((r) => {
          const err = errors[`claimed_${r.key}`];
          return err ? (
            <div key={r.key} className="text-danger">
              {r.Label}: {err}
            </div>
          ) : null;
        })}
      </div>

      <div className="d-flex justify-content-end mt-4" style={{ gap: 12 }}>
        <Button
          onClick={() => {
            handleCancel();
            setErrors({});
            setRows(SECTIONS_OLD_2024_25.map((r) => ({ ...r })));
            setFinancialYearId(null);
            setDeclarationDate(dayjs());
            setDescription("");
          }}
        >
          Cancel
        </Button>
        <Button type="primary" onClick={handleSubmit}>
          {modalType === "edit" ? "Update Declaration" : "Add Declarations"}
        </Button>
      </div>
    </Drawer>
  );
};

export default DeclarationFormModal;
